import { Controller } from "@hotwired/stimulus"

// Kind-driven admin cosmetic template form (#446, epic #394 phase 2).
//
// The form (`admin/cosmetics/templates/_form.html.erb`) is shared by all
// four cosmetic kinds (frame / title / card_bg / template) but each kind
// only consumes a subset of fields. This controller presents that subset
// dynamically: it shows the asset upload + the right metadata widgets for
// the selected kind and hides the rest, then serialises the visible
// metadata widgets into the hidden `cosmetic_template[metadata]` field
// the form actually submits.
//
// Matrix (mirrors CosmeticTemplate::METADATA_FIELDS_BY_KIND — the SERVER
// is the authority; this layer is UX only):
//   frame    → asset upload, NO metadata config
//   title    → NO upload (text), metadata: color_start/color_end/
//              text_style/animation_speed
//   card_bg  → asset upload, metadata: loop/animation_speed
//   template → asset upload, metadata: loop/animation_speed
//
// Pitfall #378: EVERY data-template-form-target below MUST live inside
// the `<div data-controller="template-form">` (the <form>) or Stimulus
// raises "Missing target element" at runtime. assert_select in the test
// suite checks attribute presence but NOT controller scope — the markup
// keeps all targets within the form element.
//
// kind immutability: on edit the kind <select> is replaced by a
// read-only display + hidden field, so `kindSelect` is absent. The
// controller reads `kindValue` (seeded from the persisted kind) as the
// fallback, and the per-kind visibility is computed once on connect.
export default class extends Controller {
  static targets = [
    "kindSelect",
    "assetSection",
    "fileInput",
    "assetHint",
    "titleMetadata",
    "lottieMetadata",
    "speedSection",
    "colorStart",
    "colorEnd",
    "textStyle",
    "loop",
    "animationSpeed",
    "animationSpeedReadout",
    "hiddenField",
  ]

  static values = {
    kind: { type: String, default: "frame" },
    initialMetadata: { type: String, default: "{}" },
  }

  // The metadata keys each kind owns. Mirrors the Ruby matrix; the
  // server re-filters so a hostile client cannot smuggle extra keys.
  static METADATA_BY_KIND = {
    frame: [],
    title: ["color_start", "color_end", "text_style", "animation_speed"],
    card_bg: ["loop", "animation_speed"],
    template: ["loop", "animation_speed"],
  }

  connect() {
    this.applyKind(this.currentKind())
    this.refreshReadouts()
    // Re-serialise from the live widgets so the hidden field reflects
    // exactly the keys this kind owns (drops any stale keys carried over
    // from a previous kind on the same persisted row).
    this.serialize()
  }

  // ---- VISIBILITY ----

  kindChanged() {
    this.applyKind(this.currentKind())
    this.serialize()
  }

  applyKind(kind) {
    const usesAsset = kind !== "title"
    const isTitle = kind === "title"
    const isLottie = kind === "card_bg" || kind === "template"
    // animation_speed is shared by title (CSS shimmer/pulse) and lottie
    // (player speed); frames carry no config at all.
    const usesSpeed = kind !== "frame"

    if (this.hasAssetSectionTarget) {
      this.toggle(this.assetSectionTarget, usesAsset)
    }
    if (this.hasTitleMetadataTarget) {
      this.toggle(this.titleMetadataTarget, isTitle)
    }
    if (this.hasLottieMetadataTarget) {
      this.toggle(this.lottieMetadataTarget, isLottie)
    }
    if (this.hasSpeedSectionTarget) {
      this.toggle(this.speedSectionTarget, usesSpeed)
    }
  }

  // ---- SERIALISATION ----

  // Reads only the widgets the current kind owns, builds the metadata
  // Hash, and writes JSON into the hidden field. Called on connect and
  // on every relevant widget change.
  serialize() {
    const kind = this.currentKind()
    const allowed = this.constructor.METADATA_BY_KIND[kind] || []
    const meta = {}

    if (allowed.includes("color_start") && this.hasColorStartTarget) {
      meta.color_start = this.colorStartTarget.value
    }
    if (allowed.includes("color_end") && this.hasColorEndTarget) {
      meta.color_end = this.colorEndTarget.value
    }
    if (allowed.includes("text_style") && this.hasTextStyleTarget) {
      meta.text_style = this.textStyleTarget.value
    }
    if (allowed.includes("loop") && this.hasLoopTarget) {
      meta.loop = this.loopTarget.checked
    }
    if (allowed.includes("animation_speed") && this.hasAnimationSpeedTarget) {
      meta.animation_speed = parseInt(this.animationSpeedTarget.value, 10)
    }

    if (this.hasHiddenFieldTarget) {
      this.hiddenFieldTarget.value = JSON.stringify(meta)
    }

    this.refreshReadouts()
  }

  refreshReadouts() {
    if (this.hasAnimationSpeedTarget && this.hasAnimationSpeedReadoutTarget) {
      this.animationSpeedReadoutTarget.textContent = this.animationSpeedTarget.value
    }
  }

  // ---- HELPERS ----

  currentKind() {
    if (this.hasKindSelectTarget) return this.kindSelectTarget.value
    return this.kindValue
  }

  toggle(el, visible) {
    if (!el) return
    el.classList.toggle("hidden", !visible)
  }
}
